import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid2";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { X } from "@phosphor-icons/react/X";
import type { Dayjs } from "dayjs";

import { dayjs } from "~mangomap-ui/lib/dayjs";

import type { QueryField } from "../tool-panel/tools/query-tool/types";

// Re-export QueryField type from QueryTool for backward compatibility

export interface DateRange {
	startDate: string;
	endDate: string;
}

export interface DateFilterProps {
	/** Field configuration (for QueryTool compatibility) */
	field?: QueryField;
	/** Simple label when not using field config */
	label?: string;
	/** Current date range value */
	value: DateRange;
	/** Callback when date range changes */
	onChange: (value: DateRange) => void;
	/** Default start date (ISO string) - used for initial value and reset */
	defaultStartDate: string;
	/** Default end date (ISO string) - used for initial value and reset */
	defaultEndDate: string;
	/** Minimum allowed date (ISO string) */
	minDate?: string;
	/** Maximum allowed date (ISO string) */
	maxDate?: string;
	/** Disabled state */
	disabled?: boolean;
	/** Error message */
	error?: string;
	/** Helper text */
	helperText?: string;
	/** Required field */
	required?: boolean;
	/** Size variant */
	size?: "small" | "medium";
}

export function DateFilter({
	field,
	label,
	value,
	onChange,
	defaultStartDate,
	defaultEndDate,
	minDate,
	maxDate,
	disabled = false,
	error,
	helperText,
	required = false,
	size = "small",
}: DateFilterProps): React.JSX.Element {
	const [dateRange, setDateRange] = React.useState<DateRange>({
		startDate: value?.startDate || defaultStartDate,
		endDate: value?.endDate || defaultEndDate,
	});

	// Warn in development if LocalizationProvider is missing
	React.useEffect(() => {
		if (process.env.NODE_ENV === "development") {
			try {
				// Test if dayjs can format a date (would fail if no LocalizationProvider)
				dayjs().format();
			} catch {
				console.warn(
					"DateFilter: LocalizationProvider is required. Please wrap your app with LocalizationProvider from @mui/x-date-pickers.",
				);
			}
		}
	}, []);

	// Determine constraints from field or props
	const effectiveMinDate = field?.minDate ?? minDate;
	const effectiveMaxDate = field?.maxDate ?? maxDate;
	const displayLabel = field?.displayName ?? label ?? "Date Range";

	// Update local state when value prop changes (from parent reset)
	React.useEffect(() => {
		if (value !== undefined) {
			setDateRange({
				startDate: value?.startDate || defaultStartDate,
				endDate: value?.endDate || defaultEndDate,
			});
		}
	}, [value, defaultStartDate, defaultEndDate]);

	// Validate date range
	const validationError = React.useMemo(() => {
		if (error) return error;

		if (required && (!dateRange.startDate || !dateRange.endDate)) {
			return "Both start and end dates are required";
		}

		if (dateRange.startDate && dateRange.endDate) {
			const start = dayjs(dateRange.startDate);
			const end = dayjs(dateRange.endDate);

			if (start.isAfter(end)) {
				return "Start date must be before end date";
			}

			if (effectiveMinDate && start.isBefore(dayjs(effectiveMinDate))) {
				return `Start date must be after ${dayjs(effectiveMinDate).format("MMM D, YYYY")}`;
			}

			if (effectiveMaxDate && end.isAfter(dayjs(effectiveMaxDate))) {
				return `End date must be before ${dayjs(effectiveMaxDate).format("MMM D, YYYY")}`;
			}
		}

		return "";
	}, [
		dateRange.startDate,
		dateRange.endDate,
		error,
		required,
		effectiveMinDate,
		effectiveMaxDate,
	]);

	const handleStartDateChange = (newValue: Dayjs | null) => {
		const dateString = newValue ? newValue.format("YYYY-MM-DD") : "";
		const newRange = { ...dateRange, startDate: dateString };
		setDateRange(newRange);
		onChange(newRange);
	};

	const handleEndDateChange = (newValue: Dayjs | null) => {
		const dateString = newValue ? newValue.format("YYYY-MM-DD") : "";
		const newRange = { ...dateRange, endDate: dateString };
		setDateRange(newRange);
		onChange(newRange);
	};

	const handleClear = () => {
		const defaultRange = {
			startDate: defaultStartDate,
			endDate: defaultEndDate,
		};
		setDateRange(defaultRange);
		onChange(defaultRange);
	};

	const hasChangedDates =
		dateRange.startDate !== defaultStartDate ||
		dateRange.endDate !== defaultEndDate;

	return (
		<Box sx={{ p: 0 }}>
			<Stack spacing={1}>
				{/* Header with label and clear button */}
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
					}}>
					<Typography
						variant="subtitle2"
						sx={{ fontWeight: "medium" }}>
						{displayLabel}
						{required && (
							<Typography
								component="span"
								color="error.main"
								sx={{ ml: 0.5 }}>
								*
							</Typography>
						)}
					</Typography>
					{hasChangedDates && (
						<Button
							variant="text"
							size="small"
							onClick={handleClear}
							disabled={disabled}
							startIcon={<X size={14} />}
							sx={{ fontSize: "0.75rem", py: 0.5, color: "text.secondary" }}
							aria-label={`Clear ${displayLabel} filter`}>
							Clear
						</Button>
					)}
				</Box>

				<Grid
					container
					spacing={2}>
					<Grid size={6}>
						<DatePicker<Dayjs>
							label="Start Date"
							value={dateRange.startDate ? dayjs(dateRange.startDate) : null}
							onChange={handleStartDateChange}
							disabled={disabled}
							minDate={effectiveMinDate ? dayjs(effectiveMinDate) : undefined}
							maxDate={effectiveMaxDate ? dayjs(effectiveMaxDate) : undefined}
							format="YYYY/MM/DD"
							slotProps={{
								field: {
									clearable: true,
								},
								textField: {
									size: size,
									fullWidth: true,
									helperText: validationError || helperText,
									error: !!validationError,
									"aria-label": `${displayLabel} start date`,
								},
								clearButton: {
									size: "small",
								},
								openPickerButton: {
									size: "small",
								},
							}}
						/>
					</Grid>
					<Grid size={6}>
						<DatePicker<Dayjs>
							label="End Date"
							value={dateRange.endDate ? dayjs(dateRange.endDate) : null}
							onChange={handleEndDateChange}
							disabled={disabled}
							minDate={effectiveMinDate ? dayjs(effectiveMinDate) : undefined}
							maxDate={effectiveMaxDate ? dayjs(effectiveMaxDate) : undefined}
							format="YYYY/MM/DD"
							slotProps={{
								field: {
									clearable: true,
								},
								textField: {
									size: size,
									fullWidth: true,
									helperText: validationError || helperText,
									error: !!validationError,
									"aria-label": `${displayLabel} end date`,
								},
								clearButton: {
									size: "small",
								},
								openPickerButton: {
									size: "small",
								},
							}}
						/>
					</Grid>
				</Grid>
			</Stack>
		</Box>
	);
}

export { type QueryField } from "../tool-panel/tools/query-tool/types";
